/**
 * Subscription Price Updater
 * Custom integration for Shopify Subscriptions that properly handles sale prices.
 */

if (!window.themeSubscriptionPriceUpdaterLoaded) {
  window.themeSubscriptionPriceUpdaterLoaded = true;

  class SubscriptionPriceUpdater {
  constructor(container) {
    this.container = container;
    this.subscriptionContainer = container.querySelector('.shopify_subscriptions_app_container');
    this.lastUpdateTime = 0;
    this.lastPlanId = '';
    this.updateDebounceDelay = 100;

    if (!this.subscriptionContainer) return;

    this.init();
  }

  init() {
    this.storeOriginalPrices();
    this.listenForVariantChanges();

    this.waitForWidget().then(() => {
      this.attachEventListeners();
      this.observeSellingPlanInputChanges();

      const selectedOption = this.getSelectedPurchaseOption();
      if (selectedOption) {
        this.handleSellingPlanChange(selectedOption);
      }
    });
  }

  storeOriginalPrices() {
    const formWrapper = this.container.querySelector('[data-form-wrapper]');

    if (!formWrapper) {
      this.originalPrice = 0;
      this.originalCompareAtPrice = 0;
      this.isProductOnSale = false;
      return;
    }

    const variantPrice = formWrapper.dataset.variantPrice;
    this.originalPrice = variantPrice ? parseInt(variantPrice, 10) : 0;

    const variantComparePrice = formWrapper.dataset.variantComparePrice;
    this.originalCompareAtPrice = variantComparePrice ? parseInt(variantComparePrice, 10) : 0;

    this.isProductOnSale = this.originalCompareAtPrice && this.originalCompareAtPrice > this.originalPrice;
  }

  listenForVariantChanges() {
    const forms = this.container.querySelectorAll('form[data-product-form]');
    forms.forEach((form) => {
      form.addEventListener('change', (event) => {
        if (event.target.closest('.shopify_subscriptions_app_container')) return;

        setTimeout(() => {
          this.storeOriginalPrices();
          this.reInitializeWidget();
        }, 400);
      });
    });

    const variantInputs = this.container.querySelectorAll('input[name="id"]');
    variantInputs.forEach((input) => {
      const observer = new MutationObserver(() => {
        setTimeout(() => {
          this.storeOriginalPrices();
          this.reInitializeWidget();
        }, 400);
      });

      observer.observe(input, {
        attributes: true,
        attributeFilter: ['value'],
      });
    });
  }

  reInitializeWidget() {
    this.waitForWidget().then(() => {
      this.attachEventListeners();
      this.observeSellingPlanInputChanges();

      const selectedOption = this.getSelectedPurchaseOption();
      if (selectedOption) {
        this.handleSellingPlanChange(selectedOption);
      }
    });
  }

  extractPrice(priceText) {
    if (!priceText) return 0;

    const cleaned = priceText
      .toString()
      .replace(/[^0-9.,]/g, '')
      .replace(',', '.');
    const price = parseFloat(cleaned);

    if (isNaN(price)) return 0;

    return Math.round(price * 100);
  }

  waitForWidget() {
    return new Promise((resolve) => {
      const radioButtons = this.getSellingPlanRadioButtons();
      if (radioButtons.length > 0) {
        resolve();
        return;
      }

      const observer = new MutationObserver(() => {
        const radios = this.getSellingPlanRadioButtons();
        if (radios.length > 0) {
          observer.disconnect();
          resolve();
        }
      });

      observer.observe(this.container, {
        childList: true,
        subtree: true,
      });

      setTimeout(() => {
        observer.disconnect();
        resolve();
      }, 5000);
    });
  }

  getSellingPlanRadioButtons() {
    return this.container.querySelectorAll('.shopify_subscriptions_app_block input[type="radio"]');
  }

  getSelectedPurchaseOption() {
    return this.container.querySelector(
      '.shopify_subscriptions_app_block:not(.shopify_subscriptions_app_block--hidden) input[type="radio"]:checked'
    );
  }

  attachEventListeners() {
    const radioButtons = this.getSellingPlanRadioButtons();

    radioButtons.forEach((radio) => {
      radio.addEventListener('change', (event) => {
        this.handleSellingPlanChange(event.target);
      });
    });
  }

  observeSellingPlanInputChanges() {
    const sellingPlanInputs = this.container.querySelectorAll('.selected-selling-plan-id');

    sellingPlanInputs.forEach((input) => {
      const observer = new MutationObserver(() => {
        const selectedOption = this.getSelectedPurchaseOption();
        if (selectedOption) {
          this.updatePrices(selectedOption);
        }
      });

      observer.observe(input, {
        attributes: true,
        attributeFilter: ['value'],
      });
    });
  }

  handleSellingPlanChange(radioButton) {
    this.updatePrices(radioButton);
    this.dispatchCustomEvent(radioButton);
  }

  updatePrices(radioButton) {
    const now = Date.now();
    const planId = radioButton.dataset.sellingPlanId;

    if (now - this.lastUpdateTime < this.updateDebounceDelay && this.lastPlanId === planId) {
      return;
    }

    this.lastUpdateTime = now;
    this.lastPlanId = planId;

    const subscriptionPriceStr = radioButton.dataset.variantPrice;
    const subscriptionCompareStr = radioButton.dataset.variantCompareAtPrice;
    const sellingPlanId = radioButton.dataset.sellingPlanId;
    const sellingPlanAdjustment = radioButton.dataset.sellingPlanAdjustment;

    if (!subscriptionPriceStr) return;

    let subscriptionPrice;
    let subscriptionComparePrice = null;

    if (typeof subscriptionPriceStr === 'string' && subscriptionPriceStr.includes('$')) {
      subscriptionPrice = this.extractPrice(subscriptionPriceStr);
      if (subscriptionCompareStr) {
        subscriptionComparePrice = this.extractPrice(subscriptionCompareStr);
      }
    } else {
      subscriptionPrice = parseInt(subscriptionPriceStr, 10);
      subscriptionComparePrice = subscriptionCompareStr ? parseInt(subscriptionCompareStr, 10) : null;
    }

    if (isNaN(subscriptionPrice) || subscriptionPrice === 0) return;

    let displayPrice = subscriptionPrice;
    let displayComparePrice = null;
    let showAsSale = false;

    const planChangesPrice = subscriptionPrice !== this.originalPrice;

    if (this.isProductOnSale) {
      if (subscriptionPrice < this.originalPrice) {
        displayPrice = subscriptionPrice;
        displayComparePrice = this.originalPrice;
        showAsSale = true;
      } else if (!planChangesPrice) {
        displayPrice = this.originalPrice;
        displayComparePrice = this.originalCompareAtPrice;
        showAsSale = true;
      } else {
        displayPrice = subscriptionPrice;
        displayComparePrice = this.originalCompareAtPrice;
        showAsSale = displayPrice < this.originalCompareAtPrice;
      }
    } else if (subscriptionPrice < this.originalPrice) {
      displayPrice = subscriptionPrice;
      displayComparePrice = this.originalPrice;
      showAsSale = true;
    } else if (!planChangesPrice) {
      displayPrice = this.originalPrice;
      displayComparePrice = null;
      showAsSale = false;
    } else {
      displayPrice = subscriptionPrice;
      displayComparePrice = null;
      showAsSale = false;
    }

    const formattedPrice = this.formatMoney(displayPrice);
    const formattedComparePrice = displayComparePrice ? this.formatMoney(displayComparePrice) : null;

    this.updateMainPriceElements(formattedPrice, formattedComparePrice, showAsSale);
    this.updateButtonPriceElements(formattedPrice, formattedComparePrice, showAsSale);

    const buttons = this.container.querySelectorAll('[data-add-to-cart]');
    buttons.forEach((button) => {
      button.dataset.subscriptionPrice = subscriptionPrice.toString();
      button.dataset.subscriptionCompareAtPrice = subscriptionComparePrice ? subscriptionComparePrice.toString() : '';
      button.dataset.sellingPlanId = sellingPlanId || '';
      button.dataset.sellingPlanAdjustment = sellingPlanAdjustment || '';
    });
  }

  updateMainPriceElements(price, comparePrice, showAsSale) {
    const priceBlocks = this.container.querySelectorAll('[id^="Price-"] .price');

    priceBlocks.forEach((priceBlock) => {
      const priceContainer = priceBlock.querySelector('.price__container');
      const regularWrap = priceBlock.querySelector('.price__regular');
      const regularPrice = priceBlock.querySelector('.price__regular .price-item--regular');
      let saleWrap = priceBlock.querySelector('.price__sale');
      let salePrice = saleWrap?.querySelector('.price-item--sale');
      let saleCompare = saleWrap?.querySelector('s.price-item--regular');

      if (!regularPrice) return;

      if (showAsSale && comparePrice) {
        priceBlock.classList.add('price--on-sale', 'product__price--sale');

        if (!saleWrap && priceContainer) {
          saleWrap = document.createElement('div');
          saleWrap.className = 'price__sale';
          saleWrap.innerHTML = `
            <span class="visually-hidden visually-hidden--inline">${window.theme?.strings?.sale || 'Sale price'}</span>
            <span class="price-item price-item--sale price-item--last"></span>
            <span class="visually-hidden visually-hidden--inline">${window.theme?.strings?.regularPrice || 'Regular price'}</span>
            <span><s class="price-item price-item--regular"></s></span>
          `;
          priceContainer.appendChild(saleWrap);
          salePrice = saleWrap.querySelector('.price-item--sale');
          saleCompare = saleWrap.querySelector('s.price-item--regular');
        }

        if (saleWrap && salePrice) {
          salePrice.innerHTML = price;

          if (saleCompare) {
            saleCompare.innerHTML = comparePrice;
          }
          saleWrap.style.display = '';
          if (regularWrap) regularWrap.style.display = 'none';
        }
      } else {
        priceBlock.classList.remove('price--on-sale', 'product__price--sale');
        regularPrice.innerHTML = price;

        if (regularWrap) regularWrap.style.display = '';
        if (saleWrap) saleWrap.style.display = 'none';
      }
    });
  }

  updateButtonPriceElements(price, comparePrice, showAsSale) {
    const buttonPriceElements = this.container.querySelectorAll('[data-product-price]');

    buttonPriceElements.forEach((priceEl) => {
      if (showAsSale && comparePrice) {
        priceEl.innerHTML = `${price} <s>${comparePrice}</s>`;
      } else {
        priceEl.innerHTML = price;
      }
    });
  }

  dispatchCustomEvent(radioButton) {
    const event = new CustomEvent('subscription:planChanged', {
      bubbles: true,
      detail: {
        price: radioButton.dataset.variantPrice,
        compareAtPrice: radioButton.dataset.variantCompareAtPrice,
        sellingPlanId: radioButton.dataset.sellingPlanId,
        sellingPlanAdjustment: radioButton.dataset.sellingPlanAdjustment,
        isProductOnSale: this.isProductOnSale,
        originalPrice: this.originalPrice,
        originalCompareAtPrice: this.originalCompareAtPrice,
        radioButton,
      },
    });

    this.container.dispatchEvent(event);
  }

  formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) {
      return '$0.00';
    }

    if (window.theme?.formatMoney) {
      return window.theme.formatMoney(cents, window.theme.moneyFormat || '${{amount}}');
    }

    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
    }

    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }
}

function initSubscriptionPriceUpdaters() {
  const containers = document.querySelectorAll('product-info');

  containers.forEach((container) => {
    if (container.dataset.subscriptionPriceUpdaterInitialized) return;

    container.dataset.subscriptionPriceUpdaterInitialized = 'true';
    new SubscriptionPriceUpdater(container);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSubscriptionPriceUpdaters);
} else {
  initSubscriptionPriceUpdaters();
}

document.addEventListener('shopify:section:load', () => {
  initSubscriptionPriceUpdaters();
});

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = SubscriptionPriceUpdater;
  }
}
